import { useEffect, useState } from 'react'
import { useI18n } from '../i18n'
import { formatClock } from '../lib/timecode'
import { prefersReducedMotion } from '../lib/motion'
import styles from './ProductPreview.module.css'

/** A short Albanian exchange, as the editor shows a real one: speaker gutter, timecode, words with the current one marked. */
const LINES = [
  { speaker: 'Arben', start: 2.1, text: 'Mirëmbrëma dhe mirë se vini në emisionin tonë. Sot flasim për zgjedhjet.' },
  { speaker: 'Elira', start: 8.4, text: 'Faleminderit për ftesën. Kandidatët i paraqitën programet dje në mbrëmje.' },
  { speaker: 'Arben', start: 15.2, text: 'Çfarë do të ndryshojë për qytetarët?' },
]
const TOTAL = 41
const WORD_SECONDS = 0.46
const STEP_MS = 430
/** Where the still frame rests when motion is reduced: a word in the middle of the second line. */
const STILL = 14

const words = LINES.map((line) => line.text.split(' '))
const flat = words.flatMap((list, line) => list.map((word, index) => ({ line, index, word })))
/** A fixed speech-density skyline for the progress bar, so the bars never change between renders. */
const BARS = Array.from({ length: 56 }, (_, i) => 22 + Math.round(Math.abs(Math.sin(i * 1.7) * 34 + Math.cos(i * 0.6) * 22)))

function useTick(): number {
  const [tick, setTick] = useState(() => (prefersReducedMotion() ? STILL : 3))
  useEffect(() => {
    if (prefersReducedMotion()) return
    const id = window.setInterval(() => setTick((n) => (n + 1) % flat.length), STEP_MS)
    return () => window.clearInterval(id)
  }, [])
  return tick
}

/** The left panel of the sign-in pages: the editor in miniature, playing. Decorative, so hidden from assistive tech. */
export function ProductPreview() {
  const { t } = useI18n()
  const tick = useTick()
  const active = flat[tick]!
  const now = LINES[active.line]!.start + active.index * WORD_SECONDS
  const progress = (tick + 1) / flat.length
  return (
    <div className={styles.window} aria-hidden="true">
      <div className={styles.top}>
        <span className={styles.file}>intervista-zgjedhjet.mp3</span>
        <span className={styles.saved}>
          <i /> {t('save.saved', { n: 4 })}
        </span>
        <span className={styles.export}>{t('export.label')}</span>
      </div>
      <div className={styles.tabs}>
        <span className={styles.tabOn}>{t('editor.tab.transcript')}</span>
        <span>{t('editor.tab.captions')}</span>
      </div>
      <div className={styles.lines}>
        {LINES.map((line, li) => (
          <div key={li} className={styles.line} data-live={li === active.line ? 'true' : undefined}>
            <div className={styles.gutter}>
              <span className={styles.who} data-who={line.speaker === 'Arben' ? 'a' : 'b'}>
                {line.speaker}
              </span>
              <span className={styles.tc}>{formatClock(line.start)}</span>
            </div>
            <p>
              {words[li]!.map((word, wi) => (
                <span key={wi} className={li === active.line && wi === active.index ? styles.hit : undefined}>
                  {word}{' '}
                </span>
              ))}
            </p>
          </div>
        ))}
      </div>
      <div className={styles.player}>
        <span className={styles.play}>❚❚</span>
        <span className={styles.clock}>
          {formatClock(now)} / {formatClock(TOTAL)}
        </span>
        <div className={styles.wave}>
          {BARS.map((h, i) => (
            <b key={i} style={{ height: `${h}%` }} data-done={(i + 1) / BARS.length <= progress ? 'true' : undefined} />
          ))}
        </div>
        <span className={styles.rate}>1×</span>
      </div>
    </div>
  )
}
